/**
 * BenchmarkDetail — the AI Buyer Journey Benchmark report.
 *
 * Renders the run's aggregate as a legible report, ordered by the four business
 * questions a marketing reader actually has (the team's 9-July-2026 report
 * flow): are we visible → who is AI surfacing instead → where do we win or lose
 * along the buyer journey → what shapes the answers. It opens with the
 * conclusion, closes with a priority action plan, and pushes the raw evidence
 * (prompt rows, AI answers, cited sources) into an appendix.
 *
 * Polls while the worker is still driving the run.
 */
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  BenchmarkAggregate,
  BenchmarkClassification,
  BenchmarkInterpretation,
  BenchmarkResult,
  BenchmarkRun,
  BuyerJourneyStage,
  CompetitorGroup,
  ReportLayoutConfig,
  ReportSectionKey,
  ShareOfVoiceEntry,
  SignalLeaderEntry,
} from '@aurora/shared';
import {
  BENCHMARK_SECTION_COPY,
  COMPETITOR_GROUPS,
  COMPETITOR_GROUP_LABELS,
  deriveBenchmarkKpis,
  funnelComparators,
  funnelComparison,
  funnelContext,
  funnelVerdict,
  isDirectorySupplierType,
  positioning,
  signalShareScopes,
  signalShares,
  sourceMixByModel,
  stageStandings,
  PROMPT_ARCHITECTURE_SHORT,
} from '@aurora/shared';
import {
  api,
  ApiClientError,
  type BenchmarkResultsPayload,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BenchmarkScorecard } from '../components/BenchmarkScorecard';
import { BenchmarkAbout } from '../components/BenchmarkAbout';
import { BenchmarkLegend } from '../components/BenchmarkLegend';
import {
  ExecutiveSynthesisCard,
  PriorityActionPlan,
  TableInsight,
} from '../components/BenchmarkInsights';
import { BenchmarkFeedbackPanel } from '../components/BenchmarkFeedbackPanel';
import { KpiBand } from '../components/charts/KpiBand';
import { PositioningPlot } from '../components/charts/PositioningPlot';
import { VisibilityFunnel } from '../components/charts/VisibilityFunnel';
import { ModelReachGrid } from '../components/charts/ModelReachGrid';
import { PromptOutcomeHeatmap } from '../components/charts/PromptOutcomeHeatmap';
import { RankedBarList } from '../components/charts/RankedBarList';
import { SourceMixByModel } from '../components/charts/SourceMixByModel';
import { StageLeaderGrid } from '../components/charts/StageLeaderGrid';
import { SignalRankPanels } from '../components/charts/SignalRankPanels';
import { SignalSharePies } from '../components/charts/SignalSharePies';
import { StageStandings } from '../components/charts/StageStandings';
import { SourceDonut } from '../components/charts/SourceDonut';
import { StageJourneyChart } from '../components/charts/StageJourneyChart';
import { ReportLayoutSettings } from '../components/ReportLayoutSettings';
import { AddSupplierPanel } from '../components/AddSupplierPanel';
import { InfoTip } from '../components/InfoTip';
import { PromptReview } from '../components/PromptReview';
import { CoveragePanel } from '../components/CoveragePanel';
import { ReportQcPanel } from '../components/ReportQcPanel';
import { benchmarkPillClass } from './Benchmarks';

/**
 * Code-owned default section order, used until the client's saved layout config
 * loads (and as a fallback). MUST mirror the server's DEFAULT_SECTIONS order in
 * services/benchmark/layout.ts — the server is the source of truth; this only
 * avoids a flash of unordered content on first paint.
 */
const DEFAULT_SECTION_ORDER: ReportSectionKey[] = [
  'executiveSynthesis',
  'shareOfVoice',
  'competitiveSets',
  'surfacedSuppliers',
  'stageVisibility',
  'signalLeaders',
  'stageSurfaced',
  'recommendationTally',
  'modelBreakdown',
  'citationSources',
  'topJournals',
  'citationSourcesByModel',
  'promptThemes',
  'actionPlan',
  'promptLevelResults',
];

/** All models, i.e. the run-wide share of voice (the filter's default). */
const ALL_MODELS = '__all__';

/**
 * Plain-language definitions for every report column.
 *
 * The three signals are measured INDEPENDENTLY (2026-07-13): one answer that
 * names, cites AND recommends a company counts once in each. They therefore
 * OVERLAP AND DO NOT SUM — Visibility is their union, not their total. Before
 * this, each answer was filed under its STRONGEST class only, which made Mention
 * and Citation leftover buckets ("cited but NOT recommended"), so the best
 * possible outcome scored zero in both and the columns ranked companies in
 * roughly reverse order of how well they were doing.
 */

/**
 * Joan's verbatim per-section copy, shared with the Markdown export so the two
 * renderings cannot drift. See `shared/src/reportCopy.ts` for the full rules —
 * in particular the banned causal vocabulary, which applies to every hard-coded
 * string in this file (the runtime detector only guards LLM-written prose).
 */
const COPY = BENCHMARK_SECTION_COPY;

const TIP = {
  company:
    'The target company and each tracked competitor. Counts are scored answers carrying each signal; the signals overlap, so they do not add up to Visibility.',
  // The four signal tooltips are Joan's wording VERBATIM (17-July-2026 doc,
  // "Suggested column/tooltip definitions") — client-facing copy, not developer
  // prose, so it is not paraphrased or "improved" here.
  visibility:
    'The company was present in the AI answer in any meaningful way. Counted once per scored answer.',
  mention: 'The company was named in the answer.',
  citation:
    'The company was named with a citation or AI cited a source owned or published by the company.',
  recommendation:
    'The company was recommended, shortlisted, ranked, selected, or presented as a supplier/product/tool to consider.',
  sourceLinks:
    'Distinct links to this company’s own website across all answers. Zero with a dash means we do not know the company’s domain, so source-backing cannot be measured for it — add its website via “Add a supplier”.',
  citedRate:
    'Of the answers that cited ANY source, the share that cited this company. Answers with no sources at all are excluded from the denominator — otherwise a run where AI cited nothing would report a misleading “0% cited” for everyone.',
  stage:
    'The buyer-journey stage of the prompts, from problem definition through final supplier selection.',
  stagePrompts: 'Number of prompts in the library for this stage.',
  stageVisibility:
    'Share of this stage’s scored answers where the target appeared at all — named, cited, or recommended (the union).',
  stageMentions:
    'Count of this stage’s answers that NAMED the target, whether or not it was also cited or recommended.',
  stageCitations:
    'Count of this stage’s answers that cited the target or a source it owns — whether or not it was also recommended.',
  stageRecommendations:
    'Count of this stage’s answers that actively recommended the target.',
  stageCompetitor:
    'Share of this stage’s scored answers where this tracked competitor appeared — a per-stage comparison baseline.',
  supplier:
    'A company the AI named for your categories that is NOT in your tracked competitor list.',
  visibleCol:
    'How many scored answers this company appeared in at all — named, cited, or recommended. The UNION of the three columns beside it, not their sum. A “scored answer” is one the model actually returned (blank cells are excluded).',
  mentionsCol:
    'How many scored answers NAMED this company — whether or not it was also cited or recommended.',
  citationsCol:
    'How many scored answers connected this company to a cited source, including sources it owns or publishes (app note, protocol, product page). Counted whether or not it was also recommended.',
  recommended: 'How many times the AI actively recommended this company.',
  model: 'The AI model in the panel that produced the answers.',
  mode: 'How this model produced its answers. “Retrieval-grounded” cited live sources in at least one answer — its recommendations are influenceable through the sources AI can find. “Memory-driven” answered from training memory with no sources — its recommendations reflect baked-in brand recall, which is durable but slow to shift.',
  scored:
    'Number of the target’s answer cells from this model that received a classification.',
  noMention: 'Answers where the target was not named at all.',
  srcType:
    'The kind of source the AI drew on (e.g. supplier app note, PubMed, review article, product directory).',
  srcAppearances:
    'How many scored answers cited this source — counted once per answer (breadth). An answer that cites 7 papers from this source still counts as 1.',
  srcCitations:
    'Total individual links to this source across all answers (volume), NOT deduped per answer. This is the number that lines up with the raw source export. 7 Nature papers + 1 Science paper in one answer = 7 vs 1 here, but 1 vs 1 under “Answers citing this source”.',
  promptStage: 'The buyer-journey stage of this prompt.',
  prompt:
    'The exact buyer question sent to the model as a clean, history-free call.',
  promptModel:
    'The model that produced this answer (with the repeat-run index when a prompt runs more than once).',
  targetClass: 'The target’s classification for this single answer.',
  recommendedCompany:
    'The company the AI actively recommended in this answer, if any.',
  othersNamed:
    'How many suppliers OTHER than you and your tracked competitors the AI named in this one answer. Click the count to expand the full list (also in the Details panel and the CSV export). Lets you scan for prompts that surfaced lots of suppliers but not you.',
  sovModel:
    'Recompute share of voice within a single AI model’s answers. The companies and their order never change — only the numbers — so you can compare one engine against another row by row.',
  mostMentions:
    'Who this stage’s answers NAMED most often — the broadest signal, and the baseline the other two columns are read against. A company named here may also be cited and recommended; the three columns rank the same answers on different signals.',
  mostRecommended:
    'Who this stage’s answers actively recommended most often — the strongest signal.',
  mostCited:
    'Who was source-backed most often in this stage — named with a cited source, or the owner of a source the AI cited. Counted whether or not the company was also recommended, so this is a genuine ranking of who AI backs with evidence.',
  srcShare:
    'This source type’s share of everything this model cited or surfaced as evidence.',
  recRank:
    'Rank by the number of scored answers that actively recommended the company.',
  recShare:
    'This company’s share of every recommendation the AI made across the whole run.',
  strongestStage:
    'The buyer-journey stage where the company is recommended most. Ties break toward the later, more decision-proximal stage.',
  whyItMatters:
    'A plain reading of the recommendation pattern, derived from the shape of the data — whether the company wins at the point of decision, across many stages, or in one application only. Rule-based, not AI-written.',
  theme:
    'What the prompts are ABOUT (a technology, workflow, or application), independent of their buyer-journey stage. Groups the prompt library into a handful of subjects so a content gap reads as one row.',
  themeGap:
    'How far the leading company’s visibility in this theme is ahead of yours. Biggest gap first — this is the opportunity list.',
  themeSources:
    'The source types the answers in this theme drew on — where to publish to close the gap.',
} as const;

const STAGE_LABELS: Record<BuyerJourneyStage, string> = {
  problem_definition: 'Stage 1: Problem Definition',
  workflow_evaluation: 'Stage 2: Workflow Evaluation',
  technology_evaluation: 'Stage 3: Technology Evaluation',
  supplier_evaluation: 'Stage 4: Supplier Evaluation',
  supplier_selection: 'Stage 5: Supplier Selection',
};

const CLASSIFICATION_LABELS: Record<BenchmarkClassification, string> = {
  no_mention: 'No Mention',
  mention: 'Mention',
  citation: 'Citation',
  recommendation: 'Recommendation',
  not_scored: 'Not Scored',
};

function classPill(c: BenchmarkClassification): JSX.Element {
  const cls =
    c === 'recommendation'
      ? 'pill pill-complete'
      : c === 'citation'
        ? 'pill pill-edited'
        : c === 'mention'
          ? 'pill pill-pending'
          : 'pill pill-draft';
  return <span className={cls}>{CLASSIFICATION_LABELS[c]}</span>;
}

function pct(v: number): string {
  if (v <= 0) return '0%';
  const p = v * 100;
  // A small-but-real rate must not round to a misleading "0%".
  if (p < 1) return '<1%';
  return `${Math.round(p)}%`;
}

/**
 * A signal cell: the rate as a percentage with the RAW COUNT beside it, so a
 * reader sees "63% (183)" — the raw numbers Joan asked to have shown, and the
 * same "% (count)" shape the Word/PDF export already uses. Every percentage is
 * the share of that company's scored answers (one prompt × one model = one
 * answer); the count is how many of those answers carried the signal.
 */
function rateCount(rate: number, count?: number): JSX.Element {
  return (
    <>
      {pct(rate)}
      {typeof count === 'number' ? (
        <span className="rate-count"> ({count})</span>
      ) : null}
    </>
  );
}

/**
 * A source-backed count, or "—" when it is NOT MEASURABLE (D3).
 *
 * Source-backing can only be seen where AI cited something and where we know the
 * company's own domain. 30 of 44 runs are web-off and cite nothing at all, so a
 * bare `0` here would read as a finding ("AI never cites them") when it is in
 * fact an absence of data. A dash says "we cannot tell", which is the truth.
 */
function citedCell(e: {
  citationsCount?: number;
  sourcedAnswers?: number;
  citedMeasurable?: boolean;
}): string {
  const count = e.citationsCount ?? 0;
  // Older aggregates predate these fields; fall back to the stored count.
  if (e.citedMeasurable === undefined && e.sourcedAnswers === undefined) {
    return String(count);
  }
  // A real, observed citation is ALWAYS shown. Source-backing has two paths — a
  // link on the company's own domain, and the AI citing a source the company
  // owns/publishes (judge-derived). The second fires even when we know no domain
  // for the company, so "not measurable" must never suppress a count we actually
  // saw. Only a genuinely blind zero becomes a dash.
  if (count > 0) return String(count);
  if (e.citedMeasurable === false) return '—';
  if ((e.sourcedAnswers ?? 0) === 0) return '—';
  return '0';
}

/** Escape a string for literal use inside a RegExp. */
function escapeReg(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/*
 * The SignalMix stacked bar was REMOVED on 2026-07-19 at Joan's request.
 * It was added in Report V2 to answer "do not let Visibility hide signal
 * quality" — but after the 2026-07-13 citation rebuild made Mention, Citation
 * and Recommendation independent, OVERLAPPING signals, a stacked bar implies
 * they are additive parts of a whole, which they are not. The signal columns
 * and the explanatory note below the table carry the same information honestly.
 */

/**
 * Is this share-of-voice entry a tracked (target or named competitor) company?
 * Prefers the aggregate's own `tracked` flag; falls back to matching the run's
 * competitor list for aggregates written before that field existed.
 */
function isTrackedEntry(
  e: ShareOfVoiceEntry,
  competitorSet: string[],
): boolean {
  if (typeof e.tracked === 'boolean') return e.tracked;
  if (e.isTarget) return true;
  const key = supplierKey(e.company);
  return competitorSet.some((c) => supplierKey(c) === key);
}

/** One "Most X" cell: up to five companies with their rate. */
function LeaderCell({
  list,
  onPick,
}: {
  list: SignalLeaderEntry[];
  onPick: (company: string) => void;
}): JSX.Element {
  if (list.length === 0) return <span className="muted">—</span>;
  return (
    <ol className="leader-list">
      {list.map((l) => (
        <li key={l.company}>
          <button
            type="button"
            className="supplier-link"
            onClick={() => onPick(l.company)}
            title={`Show the AI answers behind ${l.company}`}
          >
            {l.isTarget ? <strong>{l.company}</strong> : l.company}
          </button>{' '}
          <span className="topic-conf">{pct(l.rate)}</span>
        </li>
      ))}
    </ol>
  );
}

const OPPORTUNITY_PILL: Record<string, string> = {
  strength: 'pill pill-complete',
  contested: 'pill pill-edited',
  gap: 'pill pill-pending',
  absent: 'pill pill-draft',
};

/**
 * Highlight every supplier name that appears in a raw AI response, so the reader
 * can see in-context WHERE a supplier was surfaced (the team's "highlight where
 * Bio-Rad is" ask). The company currently filtered (if any) is emphasized more
 * strongly. Cosmetic only — over/under-highlighting never affects the data.
 */
function highlightResponse(
  text: string,
  names: string[],
  focusKey: string | null,
): ReactNode {
  const uniq = [
    ...new Set(names.map((n) => n.trim()).filter((n) => n.length >= 4)),
  ].sort((a, b) => b.length - a.length); // longest first: "Bio-Techne" beats "Bio"
  if (uniq.length === 0) return text;
  const re = new RegExp(`(${uniq.map(escapeReg).join('|')})`, 'gi');
  return text.split(re).map((part, i) => {
    // String.split with one capture group puts matches at odd indices.
    if (i % 2 === 1) {
      const focused = focusKey !== null && supplierKey(part) === focusKey;
      return (
        <mark
          key={i}
          className={
            focused ? 'supplier-mark supplier-mark-focus' : 'supplier-mark'
          }
        >
          {part}
        </mark>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

/**
 * Retrieval mode pill for the per-model breakdown: did the model ground its
 * answers in citable sources, or answer from parametric memory? Colored to
 * match the Citation pill (grounded ≈ cites) vs. the No-Mention pill (memory).
 */
function modePill(grounded: boolean): JSX.Element {
  return grounded ? (
    <span className="pill pill-edited">Retrieval-grounded</span>
  ) : (
    <span className="pill pill-draft">Memory-driven</span>
  );
}

/**
 * Loose supplier-name key for matching a surfaced supplier against the
 * per-cell `fullSupplierSet`: lowercase, strip everything but alphanumerics.
 * Approximates the server's `normalizeSupplierName` closely enough to line the
 * filtered cells up with the aggregate's appearance count, without shipping the
 * supplier dictionary to the client.
 */
function supplierKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Did the company appear at all (the aggregate's "visibility")? */
function isVisibleClass(c: BenchmarkClassification): boolean {
  return c === 'mention' || c === 'citation' || c === 'recommendation';
}

/**
 * A company drill-through on the prompt-level results table.
 *  - 'appearance':  cells where the company was NAMED (share-of-voice, surfaced
 *                   suppliers) — matched via classification for tracked
 *                   companies, full-supplier-set membership for untracked ones.
 *  - 'recommendation': cells where the company was actively RECOMMENDED
 *                   (the "Who AI recommends" tally).
 */
interface ResultFilter {
  kind: 'appearance' | 'recommendation';
  company: string;
}

const RUNNING_STATUSES = new Set([
  'pending',
  'generating',
  'collecting',
  'classifying',
  'aggregating',
]);

export function BenchmarkDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<BenchmarkRun | null>(null);
  const [aggregate, setAggregate] = useState<BenchmarkAggregate | null>(null);
  const [interpretation, setInterpretation] =
    useState<BenchmarkInterpretation | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [data, setData] = useState<BenchmarkResultsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingSources, setExportingSources] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  // Presentation audience for THIS report. 'internal' (default) is the full
  // working report — costs, review/curation tools, methodology scaffolding, the
  // interpretation, the flag column. 'client' is the demo/deliverable view: the
  // SAME component and data with internal-only chrome and not-yet-ready content
  // hidden. One report, two views — never a forked page (Ramos's "not two sets of
  // code"). Nothing here is a second computation; only visibility differs.
  //
  // This toggle is presentation ONLY — it carries zero access-control value on
  // its own (2026-09-01 finding). The real boundary is server-side: a
  // `client`-role login is rejected by every mutating/internal route and its
  // reads are scoped to its own company (see routes/benchmarks.ts's `loadRun`).
  // A `client`-role login additionally can never see the toggle or flip out of
  // client view — not because that would leak data (the server wouldn't return
  // it anyway), but so the report a customer is looking at never LOOKS like it
  // has an internal mode they could ask about.
  const { user } = useAuth();
  const isPortalClient = user?.role === 'client';
  const [viewMode, setViewMode] = useState<'internal' | 'client'>(
    isPortalClient ? 'client' : 'internal',
  );
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const clientView = isPortalClient || viewMode === 'client';
  // Reader-driven comparisons (Lisa, 2026-09-08): which company the funnel is
  // drawn against, and which population the share-of-voice pies divide.
  const [funnelRival, setFunnelRival] = useState<string | null>(null);
  const [pieScope, setPieScope] = useState<'all' | 'leaders' | 'named'>('all');
  // Which model the share-of-voice table is scoped to (ALL_MODELS = run-wide).
  const [sovModel, setSovModel] = useState<string>(ALL_MODELS);
  // Drill-through: which company the prompt-level table is filtered to (and how).
  const [filter, setFilter] = useState<ResultFilter | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  // Per-client report layout config (section order/visibility + exec tone).
  const [layout, setLayout] = useState<ReportLayoutConfig | null>(null);
  // Optional ad-hoc steer for "regenerate interpretation".
  const [guidance, setGuidance] = useState('');

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const detail = await api.getBenchmark(id);
      setRun(detail.run);
      setAggregate(detail.aggregate);
      setInterpretation(detail.interpretation);
      if (!RUNNING_STATUSES.has(detail.run.status)) {
        setData(await api.getBenchmarkResults(id));
      }
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load benchmark',
      );
    }
  }, [id]);

  // Initial load + poll while the worker is driving the run.
  useEffect(() => {
    void load();
  }, [load]);
  // Report layout config is per-client (not per-run) — fetch once on mount.
  useEffect(() => {
    api
      .getBenchmarkLayout()
      .then((r) => setLayout(r.layout))
      .catch(() => {
        /* non-fatal — fall back to the default section order */
      });
  }, []);
  useEffect(() => {
    if (!run || !RUNNING_STATUSES.has(run.status)) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [run, load]);

  // Clicking a company filters the prompt-level table to the cells behind its
  // number (toggles off if it's already the active filter).
  const applyFilter = useCallback(
    (kind: ResultFilter['kind'], company: string): void => {
      setFilter((prev) =>
        prev && prev.kind === kind && prev.company === company
          ? null
          : { kind, company },
      );
    },
    [],
  );
  const isActiveFilter = (
    kind: ResultFilter['kind'],
    company: string,
  ): boolean => filter?.kind === kind && filter.company === company;
  // Scroll the results table into view when a filter is applied.
  useEffect(() => {
    if (filter && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [filter]);

  // Regenerate the interpretation against the current aggregate (e.g. for a run
  // completed before the interpretation layer existed, or after a feedback fix).
  const onRegenerateInterpretation = async (): Promise<void> => {
    if (!id || regenerating) return;
    setRegenerating(true);
    setError(null);
    try {
      const r = await api.regenerateInterpretation(
        id,
        guidance.trim() || undefined,
      );
      setInterpretation(r.interpretation);
      setGuidance('');
    } catch (e) {
      setError(
        e instanceof ApiClientError
          ? e.message
          : 'Failed to generate interpretation',
      );
    } finally {
      setRegenerating(false);
    }
  };

  // Layer-1 one-click flag on a surfaced supplier: create a structured feedback
  // item and apply it immediately (the explicit click IS the confirmation —
  // there's no LLM interpretation to second-guess), then refresh the report.
  const flagSupplier = async (
    company: string,
    flawKind: string,
  ): Promise<void> => {
    if (!id) return;
    setError(null);
    try {
      const created = await api.createBenchmarkFeedback(id, {
        mode: 'structured',
        targetKind: 'supplier',
        targetRef: company,
        flawKind,
      });
      await api.applyBenchmarkFeedback(id, created.feedback.id);
      await load();
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to flag supplier',
      );
    }
  };

  const onExport = async (): Promise<void> => {
    if (!id || !run) return;
    setExporting(true);
    try {
      const res = await api.downloadBenchmarkCsv(id);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-benchmark-${run.companyName.replace(/\s+/g, '-').toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const onExportSources = async (): Promise<void> => {
    if (!id || !run) return;
    setExportingSources(true);
    try {
      // The workbook (Sources + Prompts tabs), not the flat CSV — Joan reviews
      // the sources alongside the prompt library that produced them.
      const res = await api.downloadBenchmarkSourcesXlsx(id);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-benchmark-${run.companyName.replace(/\s+/g, '-').toLowerCase()}-sources.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingSources(false);
    }
  };

  // The report deliverable. PDF is what a client receives (printed on the
  // server from the branded HTML); Markdown stays available internally for
  // editing or pasting. Both render from the same stored document model, so
  // they cannot disagree.
  const onExportReport = async (format: 'pdf' | 'md'): Promise<void> => {
    if (!id || !run) return;
    setExportingReport(true);
    try {
      const res =
        format === 'pdf'
          ? await api.downloadBenchmarkReportPdf(id)
          : await api.downloadBenchmarkReportMd(id);
      if (!res.ok) {
        // A raw download carries the JSON error envelope on failure (e.g. 503
        // when the server has no browser to print with). Surface its message
        // instead of saving the JSON as a broken file.
        const body = (await res.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;
        // Held by report QC: the panel says why and offers a draft copy.
        if (body?.error?.code === 'report_qc_failed') {
          setError(
            `${body.error.message ?? 'This report is held by report QC.'}` +
              (isPortalClient ? '' : ' See Report QC on this page.'),
          );
          document.getElementById('report-qc')?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        setError(
          body?.error?.message ??
            `Export failed (${res.status}). Please try again.`,
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-benchmark-${run.companyName.replace(/\s+/g, '-').toLowerCase()}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingReport(false);
    }
  };

  // Permanently delete this run (for throwaway/demo runs). Runs are immutable
  // history, so this is deliberately high-friction: the user must type the
  // company name to confirm before we call the tenant-scoped DELETE.
  const onDelete = async (): Promise<void> => {
    if (!id || !run || deleting) return;
    const typed = window.prompt(
      `Permanently delete this benchmark run for "${run.companyName}"?\n\n` +
        `This cannot be undone and removes the run, its prompts, AI answers, ` +
        `classifications and report. Type the company name to confirm:`,
    );
    if (typed === null) return; // cancelled
    if (typed.trim().toLowerCase() !== run.companyName.trim().toLowerCase()) {
      setError('The name did not match — nothing was deleted.');
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await api.deleteBenchmark(id);
      navigate('/benchmarks');
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to delete the run',
      );
      setDeleting(false);
    }
  };

  if (error) return <div className="form-error">{error}</div>;
  if (!run) return <div className="muted centered">Loading benchmark…</div>;

  // PL-2: a run whose questions are still drafting / awaiting approval is a
  // review page, not a report — nothing has been asked of the model panel yet.
  if (run.status === 'drafting' || run.status === 'draft') {
    return (
      <div className="benchmark-detail">
        <Link to="/benchmarks" className="back-link">
          ← All benchmarks
        </Link>
        <div className="page-head">
          <h1>
            {run.companyName}{' '}
            <span className={benchmarkPillClass(run.status)}>{run.status}</span>
          </h1>
        </div>
        <PromptReview runId={run.id} onApproved={() => void load()} />
      </div>
    );
  }

  const running = RUNNING_STATUSES.has(run.status);
  // PL-1 = the archived, read-only baseline (Ramos, 2026-10-20): the report
  // stays readable, but no new work is offered on it.
  const archived = run.promptLibraryId === 'PL-1';
  const promptById = new Map(data?.prompts.map((p) => [p.id, p]) ?? []);
  const responseByCell = new Map(
    data?.responses.map((r) => [`${r.promptId}#${r.model}#${r.runIndex}`, r]) ??
      [],
  );
  const untracked =
    aggregate?.surfacedSuppliers.filter((s) => !s.tracked) ?? [];
  // The client's own row in the supplier-detail table (Joan, 2026-07-10): a list
  // of everyone AI surfaced is unreadable without your own numbers beside it.
  // The server pins it; older aggregates simply have no isTarget row and the
  // table renders exactly as before.
  const targetSurfaced =
    aggregate?.surfacedSuppliers.find((s) => s.isTarget) ?? null;
  // Segment supplier directories / marketplaces out of the head-to-head so a
  // Biocompare appearance never reads as a competitor outranking the target
  // (Joan 2026-06-19 rule). They still count and are shown in their own group.
  const untrackedSuppliers = untracked.filter(
    (s) => !isDirectorySupplierType(s.supplierType),
  );
  const untrackedDirectories = untracked.filter((s) =>
    isDirectorySupplierType(s.supplierType),
  );
  // Names the aggregate CONFIRMED as suppliers (post-exclusion). The response
  // highlighter is restricted to these so tools that slipped into a cell's
  // stored full_supplier_set (e.g. GATK on a pre-cleanup run) are never marked
  // as suppliers — re-aggregation cleans the aggregate even when the stored
  // per-cell set is stale.
  const confirmedSupplierKeys = new Set<string>();
  for (const e of aggregate?.shareOfVoice.entries ?? [])
    confirmedSupplierKeys.add(supplierKey(e.company));
  for (const s of aggregate?.surfacedSuppliers ?? [])
    confirmedSupplierKeys.add(supplierKey(s.company));
  // Tracked-competitor columns for the per-stage table (up to 4, parent-named).
  const stageCompetitors =
    aggregate?.stageVisibility.entries[0]?.competitors ?? [];

  // What was actually TRACKED, after input normalization. A competitor typed as
  // a URL, as a directory, or as two sites pasted onto one line is dropped (see
  // normalizeCompetitorInput on the server), so the RAW input can overstate the
  // tracked set — and printing the raw input is how a garbage string ends up in
  // front of the client. Read the tracked set from the aggregate, which is the
  // cleaned truth; fall back to the raw input only for aggregates written before
  // the `tracked` flag existed.
  const sovKnowsTracked = (aggregate?.shareOfVoice.entries ?? []).some(
    (e) => typeof e.tracked === 'boolean',
  );
  const trackedCompetitors = sovKnowsTracked
    ? (aggregate?.shareOfVoice.entries ?? [])
        .filter((e) => e.tracked && !e.isTarget)
        .map((e) => e.company)
    : run.competitorSet;
  const droppedCompetitors = sovKnowsTracked
    ? Math.max(0, run.competitorSet.length - trackedCompetitors.length)
    : 0;

  // --- Share of voice, optionally scoped to one panel model -----------------
  // The per-model tables carry the SAME companies in the SAME order, so the
  // filter changes only the numbers. Falls back to the run-wide table on
  // aggregates written before per-model share of voice existed.
  const sovAll = aggregate?.shareOfVoice.entries ?? [];
  const sovModels = aggregate?.shareOfVoiceByModel ?? [];
  const sovEntries =
    sovModel === ALL_MODELS
      ? sovAll
      : (sovModels.find((m) => m.model === sovModel)?.entries ?? sovAll);

  // --- The named vs. unnamed competitive sets (report section 2) ------------
  // Built from the RUN-WIDE share of voice, never from `sovEntries`: the
  // "filter by LLM" control belongs to the share-of-voice table above, and a
  // control in one section must not silently rewrite another section's numbers.
  //
  // Directories/marketplaces are a source type, not a competitor, so they never
  // enter either set (they have their own sub-table under surfaced suppliers).
  const namedSet = sovAll.filter((e) => isTrackedEntry(e, run.competitorSet));
  // Axis 2 (2026-07-17): the client-assigned competitor group, with a three-layer
  // fallback so historical runs (no `competitorGroup` on the aggregate, no
  // `competitorMeta` on the run) still render grouped by supplier vs directory.
  const competitorGroupOf = (e: ShareOfVoiceEntry): CompetitorGroup =>
    e.competitorGroup ??
    run.competitorMeta?.[e.company]?.group ??
    (isDirectorySupplierType(e.supplierType)
      ? 'directory_marketplace'
      : 'supplier');
  const namedTarget = namedSet.find((e) => e.isTarget) ?? null;
  const namedCompetitors = namedSet.filter((e) => !e.isTarget);
  // Non-empty groups in canonical order. Show group sub-headers only when the
  // set actually spans 2+ groups; a single-group set (the common all-supplier
  // case) renders flat, so grouping never adds noise where there's nothing to
  // separate.
  const namedByGroup = COMPETITOR_GROUPS.map((g) => ({
    group: g,
    rows: namedCompetitors.filter((e) => competitorGroupOf(e) === g),
  })).filter((x) => x.rows.length > 0);
  const showGroupHeaders = namedByGroup.length >= 2;
  const unnamedSet = sovAll
    .filter((e) => !isTrackedEntry(e, run.competitorSet))
    .filter((e) => !isDirectorySupplierType(e.supplierType));
  // The unnamed set is ranked BY RECOMMENDATION — who AI is actively putting
  // forward that you never even named.
  const topUnnamed = [...unnamedSet]
    .sort(
      (a, b) =>
        b.recommendationRate - a.recommendationRate ||
        b.visibilityRate - a.visibilityRate,
    )
    .slice(0, 5);
  const targetEntry = sovAll.find((e) => e.isTarget);
  // The denominator, stated explicitly (Joan 2026-07-17: "49% of what?").
  // DERIVED from the run, never hard-coded to 25x4 — a teaser is 5 prompts and
  // the panel size is configurable, so a fixed "100 scored answers" would be a
  // lie on most runs. scoredCells is the real per-company denominator.
  const modelCount = aggregate?.modelBreakdown.entries.length ?? 0;
  const stageCount = aggregate?.stageVisibility.entries.length ?? 0;
  const scoredAnswers = targetEntry?.scoredCells ?? sovAll[0]?.scoredCells ?? 0;
  const promptCount =
    modelCount > 0 ? Math.round(scoredAnswers / modelCount) : 0;
  const showDenominatorNote = scoredAnswers > 0 && modelCount > 0;
  // Decision KPIs + the V2 chart inputs (2026-09-08). All DERIVED from the
  // aggregate this page already loaded — no new server field, no migration, no
  // backfill — so every historical run gains them the moment this deploys. The
  // arithmetic lives in `@aurora/shared` so the Markdown deliverable states the
  // same numbers (page/document drift has bitten this codebase three times).
  //
  // `internalKpis` gates audience-restricted tiles: model-coverage blackout is
  // internal-only by default, because it usually traces back to the client's own
  // robots.txt and deserves a conversation rather than a cold dashboard tile.
  const kpiSet = deriveBenchmarkKpis(aggregate, {
    internalKpis: !clientView,
  });
  // Chart inputs for the sections that are a "list ranked by one number". They
  // all feed the same RankedBarList so the report reads as one system rather
  // than four separately-invented charts.
  const unnamedChartData = (
    targetEntry ? [targetEntry, ...topUnnamed] : topUnnamed
  )
    .map((e) => ({
      label: e.company,
      value: e.visibleCount ?? 0,
      highlight: e.isTarget,
      tooltip: `${e.company}: visible in ${e.visibleCount ?? 0} of ${e.scoredCells} answers · ${e.recommendationsCount ?? 0} recommendations`,
    }))
    .sort((a, b) => b.value - a.value);
  const sourceMixes = aggregate ? sourceMixByModel(aggregate) : [];
  // Narrative shapes — each of these makes an argument the tables cannot.
  const positioningData = aggregate ? positioning(aggregate) : null;
  // The funnel opens on the strongest NAMED competitor (the leader is one
  // selection away) — see funnelComparators for why. A stale selection from a
  // previous run falls back to the default rather than drawing nothing.
  const funnelChoices = aggregate
    ? funnelComparators(aggregate)
    : { options: [], defaultCompany: null };
  const funnelRivalEffective =
    funnelRival && funnelChoices.options.some((o) => o.company === funnelRival)
      ? funnelRival
      : funnelChoices.defaultCompany;
  const funnels = aggregate
    ? funnelComparison(aggregate, funnelRivalEffective)
    : [];
  const funnelCtx = aggregate ? funnelContext(aggregate) : null;
  const funnelText = funnelVerdict(funnels, funnelChoices);
  const pieScopes = aggregate ? signalShareScopes(aggregate) : [];
  const pieScopeEffective = pieScopes.some((s) => s.key === pieScope)
    ? pieScope
    : 'all';
  const shareOfVoicePies = aggregate
    ? signalShares(aggregate, { scope: pieScopeEffective })
    : [];
  const standings = aggregate
    ? stageStandings(aggregate)
    : {
        top: 5,
        stages: [],
        strongest: null,
        weakest: null,
        shortestPath: null,
        leaders: [],
        rival: null,
      };
  // Joan's competitive-section logic (2026-07-17). Two scenarios, different
  // headings — the client is ALWAYS a row (it is the reference point), but it
  // must never be *labelled* an "additional AI-surfaced company", which is what
  // the single "Additional Companies AI Surfaced" heading did when the target
  // was pinned to the top of that table.
  //
  //   named competitors provided -> "Client vs. Named Competitive Set"
  //                              +  "Client vs. Additional AI-Surfaced Companies"
  //   none provided              -> "Client vs. AI-Surfaced Supplier Landscape"
  const hasNamedSet = namedCompetitors.length > 0;
  const surfacedHeading = hasNamedSet
    ? 'Client vs. Additional AI-Surfaced Companies'
    : 'Client vs. AI-Surfaced Supplier Landscape';
  const surfacedIntro = hasNamedSet
    ? 'In addition to the named competitive set, AI may surface other companies in response to relevant buyer, workflow, or supplier-selection prompts. This table compares your company against those additional AI-surfaced companies to identify overlooked competitors, adjacent alternatives, or companies that may warrant tracking in future benchmark runs.'
    : 'No named competitive set was provided for this benchmark. This table compares your company against the companies AI surfaced most often in the tested answers.';
  // One Named-Competitive-Set row. Extracted so the flat and grouped renderings
  // share identical markup — the group sub-headers only change the ORDER and
  // insert label rows, never the numbers or the per-row behaviour.
  const namedRow = (e: ShareOfVoiceEntry): JSX.Element => (
    <tr
      key={e.company}
      className={
        isActiveFilter('appearance', e.company) ? 'row-active' : undefined
      }
    >
      <td className={e.isTarget ? 'lead-name' : undefined}>
        <button
          type="button"
          className="supplier-link"
          onClick={() => applyFilter('appearance', e.company)}
          title={`Show the AI answers where ${e.company} appeared`}
        >
          {e.company}
        </button>
        {e.isTarget ? ' (you)' : ''}
      </td>
      <td>{pct(e.visibilityRate)}</td>
      <td>{rateCount(e.mentionRate, e.mentionsCount)}</td>
      <td>{rateCount(e.citationRate, e.citationsCount)}</td>
      <td>{rateCount(e.recommendationRate, e.recommendationsCount)}</td>
    </tr>
  );
  // A cell "names" a company: the target/competitor via its classification,
  // any other supplier via full-supplier-set membership (loose-key matched).
  const appearedIn = (r: BenchmarkResult, company: string): boolean => {
    if (company === run.companyName)
      return isVisibleClass(r.targetClassification);
    if (company in r.competitorClassifications)
      return isVisibleClass(
        r.competitorClassifications[company] ?? 'no_mention',
      );
    const key = supplierKey(company);
    return r.fullSupplierSet.some((name) => supplierKey(name) === key);
  };
  // A cell "recommends" a company: it appears in the recommended-company value
  // (a "A + B" join), loose-key matched.
  const recommendedIn = (r: BenchmarkResult, company: string): boolean => {
    if (!r.recommendedCompany) return false;
    const key = supplierKey(company);
    return r.recommendedCompany.split('+').some((p) => supplierKey(p) === key);
  };

  // When a company is selected, narrow the prompt-level results to the cells
  // behind its number; otherwise show everything.
  const filterKey = filter ? supplierKey(filter.company) : null;
  const allResults = data?.results ?? [];
  const visibleResults = !filter
    ? allResults
    : filter.kind === 'recommendation'
      ? allResults.filter((r) => recommendedIn(r, filter.company))
      : allResults.filter((r) => appearedIn(r, filter.company));

  return (
    <div className="benchmark-detail">
      <Link to="/benchmarks" className="back-link">
        ← All benchmarks
      </Link>
      <div className="page-head">
        <h1>
          {run.companyName}{' '}
          <span className={benchmarkPillClass(run.status)}>{run.status}</span>
        </h1>
        <div className="head-actions">
          {!running && !isPortalClient && (
            <button
              type="button"
              className="btn"
              onClick={() => setViewMode(clientView ? 'internal' : 'client')}
              title={
                clientView
                  ? 'Switch back to the full internal report (costs, review tools, methodology, interpretation).'
                  : 'Preview the report as a client sees it — internal tools, costs and not-yet-ready sections hidden. Same data, same report.'
              }
            >
              {clientView ? '↩ Internal view' : '👁 View as client'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void onExportReport('pdf')}
            disabled={exportingReport || running}
            title="The whole report as a branded PDF — the client deliverable. Takes a few seconds to print."
          >
            {exportingReport ? 'Preparing…' : 'Download report (PDF)'}
          </button>
          {!clientView && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onExportReport('md')}
              disabled={exportingReport || running}
              title="The same report as plain Markdown, for editing or pasting elsewhere"
            >
              Markdown (.md)
            </button>
          )}
          {!clientView && (
            <button
              type="button"
              className="btn"
              onClick={() => void onExport()}
              disabled={exporting || running}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => void onExportSources()}
            disabled={exportingSources || running}
            title="Excel workbook — every cited source per prompt with its auto-assigned category (for source classification / training), plus the run's prompt library on a second tab"
          >
            {exportingSources ? 'Exporting…' : 'Export sources'}
          </button>
          {!clientView && !running && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void onDelete()}
              disabled={deleting}
              title="Permanently delete this run (for throwaway/demo runs). Requires typing the company name to confirm."
            >
              {deleting ? 'Deleting…' : 'Delete run'}
            </button>
          )}
        </div>
      </div>

      <p className="muted">
        {run.modelPanel.join(', ')} · {run.conditions.runsPerPrompt} run(s) per
        prompt · web search {run.conditions.webSearch ? 'on' : 'off'} ·{' '}
        {run.conditions.depth} depth · created{' '}
        {new Date(run.createdAt).toLocaleString()}
        {trackedCompetitors.length > 0 && (
          <> · tracking: {trackedCompetitors.join(', ')}</>
        )}
        {/* Stated applications are a CLIENT INPUT, not internal chrome — they
            show in the client view too, so the client can see the techniques
            the prompt library was written against. */}
        {(run.applications ?? []).length > 0 && (
          <> · applications: {(run.applications ?? []).join(', ')}</>
        )}
      </p>

      {droppedCompetitors > 0 && (
        <div className="form-notice">
          {droppedCompetitors} of the {run.competitorSet.length} competitor
          entries on this run could not be tracked as a competitor — an entry
          typed as a website, a supplier directory or marketplace (a source, not
          a competitor), or two companies pasted onto one line. They are
          excluded from the competitive tables rather than scored as a company.
          Companies AI surfaced on its own still appear below, counted from the
          answers that actually named them.
        </div>
      )}

      {run.usage && !clientView && (
        <div className="batch-strip">
          <div className="batch-chip">
            <span className="batch-name">
              Run cost: ${run.usage.total.cost.toFixed(2)}
              {running ? ' so far' : ''}
            </span>
            <span className="muted">
              {run.usage.total.calls} LLM calls ·{' '}
              {Math.round(run.usage.total.promptTokens / 1000)}k in /{' '}
              {Math.round(run.usage.total.completionTokens / 1000)}k out tokens
            </span>
          </div>
          {Object.entries(run.usage.collection).map(([model, u]) => (
            <div key={model} className="batch-chip">
              <span className="batch-name">{model}</span>
              <span className="muted">
                ${u.cost.toFixed(2)} · {u.calls} calls
              </span>
            </div>
          ))}
          {run.usage.judge.calls > 0 && (
            <div className="batch-chip">
              <span className="batch-name">classifier</span>
              <span className="muted">
                ${run.usage.judge.cost.toFixed(2)} · {run.usage.judge.calls}{' '}
                calls
              </span>
            </div>
          )}
          {/* Always shown on a completed run (even at $0) so the editing-cost
              line is a predictable part of the strip. */}
          <div
            className="batch-chip"
            title="LLM cost of the report interpretation plus every regeneration and feedback edit since the run — tracked separately from the run cost above. Layout changes and one-click flags are free."
          >
            <span className="batch-name">
              Interpretation &amp; edits: $
              {(run.usage.editing?.cost ?? 0).toFixed(2)}
            </span>
            <span className="muted">
              {run.usage.editing?.calls ?? 0} LLM calls
            </span>
          </div>
        </div>
      )}

      {running && (
        <div className="form-notice">
          Benchmark in progress ({run.status}) — this page refreshes
          automatically. Collection paces itself against model rate limits, so a
          full multi-model run can take several minutes.
        </div>
      )}
      {run.status === 'failed' && run.error && (
        <div className="form-error">Run failed: {run.error}</div>
      )}
      {archived && (
        <div className="form-notice archive-banner">
          <strong>Archive · PL-1 question library.</strong> This report was
          produced with the earlier benchmark question set (before October
          2026). It is kept for reference and is read-only; its figures are not
          directly comparable with reports produced under the current library.
        </div>
      )}
      {!clientView && run.promptLibraryId && (
        <p className="muted library-provenance">
          Questions: {PROMPT_ARCHITECTURE_SHORT[run.promptArchitecture ?? 'PL-2']} · revision{' '}
          {run.promptLibraryRevision ?? 1}
          {run.promptsApprovedAt
            ? ` · approved ${new Date(run.promptsApprovedAt).toLocaleString()}${
                run.promptsApprovedByEmail ? ` by ${run.promptsApprovedByEmail}` : ''
              }`
            : run.promptLibraryId === 'PL-1'
              ? ' · archived baseline (before pre-run approval existed)'
              : ''}
          {run.promptLibraryId !== 'PL-1' && !running && (
            <>
              {' '}
              <button
                type="button"
                className="btn btn-small btn-ghost"
                title="A post-approval change never edits this run: it starts a new draft revision with the same questions, to review and approve again."
                onClick={() =>
                  void api
                    .revisePromptLibrary(run.id)
                    .then((r) => navigate(`/benchmarks/${r.run.id}`))
                    .catch((e: unknown) =>
                      setError(e instanceof ApiClientError ? e.message : 'Could not start a revision'),
                    )
                }
              >
                Revise questions (new revision)
              </button>
            </>
          )}
          {run.purpose === 'source_intelligence' && (
            <>
              <br />
              Source Intelligence · {run.datasetId} · {run.datasetMarket}
              {run.researchSettings
                ? ` · web ${run.researchSettings.webSearch ? 'on' : 'OFF'} · ${run.researchSettings.resolvedSlugs.join(', ')} · ${run.researchSettings.runsPerPrompt} run(s)/prompt · web cap ${run.researchSettings.webMaxResults || 'default'} / context ${run.researchSettings.webContextSize || 'default'} (frozen ${new Date(run.researchSettings.frozenAt).toLocaleDateString()})`
                : ' · settings are frozen at approval'}
            </>
          )}
        </p>
      )}
      {!clientView && run.purpose === 'source_intelligence' && run.status === 'complete' && (
        <CoveragePanel runId={run.id} />
      )}
      {!clientView && !isPortalClient && run.status === 'complete' && (
        <ReportQcPanel runId={run.id} companyName={run.companyName} onError={setError} />
      )}
      {run.warnings.length > 0 && !running && (
        <details className="form-notice">
          <summary>
            {run.warnings.length} non-fatal warning(s) on this run — some cells
            may be marked Not Scored, or an input may have been adjusted.
          </summary>
          <ul>
            {run.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}

      {(() => {
        // The CODE owns which report sections exist and how each one renders;
        // the per-client layout config (DB data) only chooses which appear and
        // in what order — Joan's git-safe structure control. Each node
        // self-guards on its data, so a section with nothing to show renders
        // nothing. (Scorecard, the regenerate control, and the glossary are
        // structural and always shown — they are not part of this set.)
        const sectionNodes: Record<ReportSectionKey, JSX.Element | null> = {
          executiveSynthesis:
            aggregate && interpretation ? (
              <ExecutiveSynthesisCard exec={interpretation.executive} />
            ) : null,

          shareOfVoice: aggregate ? (
            <>
              <h2 className="section-head">
                <span>AI Visibility Signal Summary</span>
                {sovModels.length > 1 && (
                  <label className="sov-filter">
                    <span className="muted">
                      Filter by model <InfoTip text={TIP.sovModel} />
                    </span>
                    <select
                      value={sovModel}
                      onChange={(e) => setSovModel(e.target.value)}
                    >
                      <option value={ALL_MODELS}>All models</option>
                      {sovModels.map((m) => (
                        <option key={m.model} value={m.model}>
                          {m.model}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </h2>
              {/* Joan's wording verbatim (17-July doc §2, "Copy for Table").
                  The paraphrase this replaced reintroduced two retired terms —
                  "that appearance" (the metric is Visibility) and "signal mix"
                  (the stacked bar was removed on 2026-07-19 precisely because
                  overlapping signals are not parts of a whole). */}
              <p className="section-note">{COPY.shareOfVoice.intro}</p>
              <p className="muted section-note">
                Each percentage is the share of that company’s scored answers —
                one prompt run on one AI model counts as one answer — with the
                raw count shown in parentheses.
              </p>
              <p className="section-note">
                <strong>Use this data to:</strong>{' '}
                {COPY.shareOfVoice.useThisData}
              </p>
              {sovModel !== ALL_MODELS && (
                <p className="form-notice">
                  Showing this table within <strong>{sovModel}</strong> only.
                  The companies and their order are unchanged — compare the
                  numbers against another model, or against All models.
                </p>
              )}
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>
                      Company <InfoTip text={TIP.company} />
                    </th>
                    <th>
                      Visibility <InfoTip text={TIP.visibility} />
                    </th>
                    <th>
                      Mention <InfoTip text={TIP.mention} />
                    </th>
                    <th>
                      Citation <InfoTip text={TIP.citation} />
                    </th>
                    <th>
                      Recommendation{' '}
                      <InfoTip text={TIP.recommendation} align="end" />
                    </th>
                    {/* Signal mix removed 2026-07-19 (Joan): Mention, Citation
                        and Recommendation OVERLAP, so a stacked bar implies
                        they are additive parts of a whole. The columns stay;
                        only the misleading visual goes. */}
                  </tr>
                </thead>
                <tbody>
                  {sovEntries.slice(0, 10).map((e) => (
                    <tr
                      key={e.company}
                      className={
                        isActiveFilter('appearance', e.company)
                          ? 'row-active'
                          : undefined
                      }
                    >
                      <td className={e.isTarget ? 'lead-name' : undefined}>
                        <button
                          type="button"
                          className="supplier-link"
                          onClick={() => applyFilter('appearance', e.company)}
                          title={`Show the AI answers where ${e.company} appeared`}
                        >
                          {e.company}
                        </button>
                        {e.isTarget ? ' (you)' : ''}
                      </td>
                      <td>{pct(e.visibilityRate)}</td>
                      <td>{rateCount(e.mentionRate, e.mentionsCount)}</td>
                      <td>{rateCount(e.citationRate, e.citationsCount)}</td>
                      <td>
                        {rateCount(
                          e.recommendationRate,
                          e.recommendationsCount,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Share of voice per signal as pie charts (Ramos, 2026-09-08;
                  Lisa asked the same on 09-01). Legitimate part-to-whole: each
                  mention/citation/recommendation is one (answer, company) event
                  belonging to exactly one company. The denominator is EVERY
                  company AI named, not this table's top-N — see `signalShares`. */}
              <SignalSharePies
                shares={shareOfVoicePies}
                scopes={pieScopes}
                scope={pieScopeEffective}
                onScopeChange={setPieScope}
              />
              {/* The ranked panels stay underneath as the precise read: a pie
                  cannot separate values three points apart, and this is where
                  "who is winning citations" is actually answered. */}
              <SignalRankPanels
                rankings={kpiSet.signalRankings}
                targetName={targetEntry?.company}
              />
              {showDenominatorNote && (
                <p className="muted signal-quality-note">
                  This benchmark includes {promptCount} prompts
                  {stageCount > 0
                    ? ` across ${stageCount} buyer-journey stages`
                    : ''}
                  , each tested on {modelCount}
                  {modelCount === 1 ? ' AI model' : ' AI models'} —{' '}
                  <strong>{scoredAnswers} scored AI answers</strong>.
                  Percentages in this table use those scored answers as the
                  denominator: a company shown at 49% (
                  {Math.round(0.49 * scoredAnswers)}) was present in{' '}
                  {Math.round(0.49 * scoredAnswers)} of {scoredAnswers}. Blank,
                  failed, or unusable responses are marked Not Scored and
                  excluded from the denominator.
                </p>
              )}
              <p className="muted signal-quality-note">
                Visibility is the umbrella metric and is counted once per scored
                answer. Mention, Citation and Recommendation are measured
                independently and may overlap — one answer that names a company,
                cites its website and recommends it counts in all three columns,
                but only once for Visibility.{' '}
                <strong>Do not add the signal columns across a row;</strong>{' '}
                read down each column to compare companies by signal type.
              </p>
              <TableInsight insight={interpretation?.tables.shareOfVoice} />
            </>
          ) : null,

          competitiveSets:
            aggregate && (namedSet.length > 1 || topUnnamed.length > 0) ? (
              <>
                <h2 className="section-head">AI-Surfaced Competitive Set</h2>
                <p className="section-note">
                  <strong>Use this data to:</strong> Evaluate whether your
                  competitive set reflects how AI is actually framing the
                  supplier landscape, identify overlooked competitors or
                  adjacent supplier alternatives, and determine whether
                  additional companies should be tracked, analyzed, or addressed
                  in future content and positioning strategy.
                </p>
                {hasNamedSet && (
                  <>
                    <h3 className="section-subhead">
                      Client vs. Named Competitive Set
                    </h3>
                    <p className="section-note">
                      This table compares your company against the named
                      competitors provided for this benchmark. Use it to
                      understand how your AI visibility, mentions, citations,
                      and recommendations compare with the companies you
                      specifically wanted to track.
                    </p>
                    <table className="leads-table">
                      <thead>
                        <tr>
                          <th>
                            Company <InfoTip text={TIP.company} />
                          </th>
                          <th>
                            Visibility <InfoTip text={TIP.visibility} />
                          </th>
                          <th>
                            Mention <InfoTip text={TIP.mention} />
                          </th>
                          <th>
                            Citation <InfoTip text={TIP.citation} />
                          </th>
                          <th>
                            Recommendation{' '}
                            <InfoTip text={TIP.recommendation} align="end" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {namedTarget && namedRow(namedTarget)}
                        {showGroupHeaders
                          ? namedByGroup.map((grp) => (
                              <Fragment key={grp.group}>
                                <tr className="group-subhead">
                                  <td colSpan={5}>
                                    {COMPETITOR_GROUP_LABELS[grp.group]}
                                  </td>
                                </tr>
                                {grp.rows.map(namedRow)}
                              </Fragment>
                            ))
                          : namedCompetitors.map(namedRow)}
                      </tbody>
                    </table>
                    {/* Not a second ranked bar: reach against conversion, so
                        the reader gets a POSITION and a verdict rather than
                        another restatement of one column. */}
                    {positioningData && (
                      <PositioningPlot data={positioningData} />
                    )}
                  </>
                )}
                {topUnnamed.length > 0 && (
                  <>
                    <h3 className="section-subhead">{surfacedHeading}</h3>
                    <p className="section-note">
                      {surfacedIntro} Ranked by recommendation rate to highlight
                      the alternatives AI may be positioning as relevant
                      options. Your company is included as the reference row for
                      comparison.
                    </p>
                    <table className="leads-table">
                      <thead>
                        <tr>
                          <th>
                            Company <InfoTip text={TIP.supplier} />
                          </th>
                          <th>
                            Visibility <InfoTip text={TIP.visibility} />
                          </th>
                          <th>
                            Mention <InfoTip text={TIP.mention} />
                          </th>
                          <th>
                            Citation <InfoTip text={TIP.citation} />
                          </th>
                          <th>
                            Recommendation{' '}
                            <InfoTip text={TIP.recommendation} align="end" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(targetEntry
                          ? [targetEntry, ...topUnnamed]
                          : topUnnamed
                        ).map((e) => (
                          <tr
                            key={e.company}
                            className={
                              isActiveFilter('appearance', e.company)
                                ? 'row-active'
                                : undefined
                            }
                          >
                            <td
                              className={e.isTarget ? 'lead-name' : undefined}
                            >
                              <button
                                type="button"
                                className="supplier-link"
                                onClick={() =>
                                  applyFilter('appearance', e.company)
                                }
                                title={`Show the AI answers where ${e.company} appeared`}
                              >
                                {e.company}
                              </button>
                              {e.isTarget ? ' (you)' : ''}
                            </td>
                            <td>{pct(e.visibilityRate)}</td>
                            <td>{rateCount(e.mentionRate, e.mentionsCount)}</td>
                            <td>
                              {rateCount(e.citationRate, e.citationsCount)}
                            </td>
                            <td>
                              {rateCount(
                                e.recommendationRate,
                                e.recommendationsCount,
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* The companies AI raised unprompted, ranked. This is the
                        set that actually contests the category — the client's
                        own named set is usually not it. */}
                    <RankedBarList
                      data={unnamedChartData}
                      caption="Companies AI surfaced without being asked, ranked by the answers they appeared in."
                    />
                  </>
                )}
              </>
            ) : null,

          stageVisibility: aggregate ? (
            <>
              <h2 className="section-head">
                Client Visibility by Buyer-Journey Stage
              </h2>
              <p className="section-note">
                This table shows how the target company performs at each stage
                of the scientist’s buying journey, from early problem definition
                through final supplier selection. It reveals where the company
                is visible, absent, cited, or recommended as buyer questions
                become more specific.
              </p>
              <p className="section-note">
                <strong>Use this data to:</strong> Identify the stages where
                your company is strongest or weakest in AI-generated answers.
                Use early-stage gaps to guide educational and problem-framing
                content, mid-stage gaps to strengthen workflow and technology
                comparison content, and late-stage gaps to improve
                supplier-selection, proof, validation, and differentiation
                messaging.
              </p>
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>
                      Stage <InfoTip text={TIP.stage} />
                    </th>
                    <th>
                      Prompts <InfoTip text={TIP.stagePrompts} />
                    </th>
                    <th>
                      Visibility <InfoTip text={TIP.stageVisibility} />
                    </th>
                    <th>
                      Mention <InfoTip text={TIP.stageMentions} />
                    </th>
                    <th>
                      Citation <InfoTip text={TIP.stageCitations} />
                    </th>
                    <th>
                      Recommendation{' '}
                      <InfoTip
                        text={TIP.stageRecommendations}
                        align={
                          stageCompetitors.length === 0 ? 'end' : undefined
                        }
                      />
                    </th>
                    {stageCompetitors.map((c, i) => (
                      <th key={c.company}>
                        {c.company}{' '}
                        <InfoTip
                          text={TIP.stageCompetitor}
                          align={
                            i === stageCompetitors.length - 1
                              ? 'end'
                              : undefined
                          }
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aggregate.stageVisibility.entries.map((s) => (
                    <tr key={s.stage}>
                      <td>{STAGE_LABELS[s.stage]}</td>
                      <td>{s.prompts}</td>
                      <td>{pct(s.visibilityRate)}</td>
                      <td>{s.mentions}</td>
                      <td>{s.citations}</td>
                      <td>{s.recommendations}</td>
                      {stageCompetitors.map((c) => {
                        const cv = (s.competitors ?? []).find(
                          (x) => x.company === c.company,
                        );
                        return (
                          <td key={c.company}>
                            {cv ? pct(cv.visibilityRate) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* The journey-stage dashboard Lisa asked for (2026-09-01): "show
                  how [the client] does against its named competitor set at stage
                  one, and then one for stage two... is there a journey stage
                  kind of dashboard in itself that kind of aggregates". One chart
                  across all five stages rather than five charts, so the SHAPE of
                  the funnel is readable at a glance. */}
              <StageJourneyChart
                rows={kpiSet.stageComparison}
                targetName={targetEntry?.company ?? run.companyName}
              />
              <TableInsight insight={interpretation?.tables.stageVisibility} />
            </>
          ) : null,

          signalLeaders:
            aggregate &&
            aggregate.signalLeaders?.some(
              (s) =>
                (s.mostMentions?.length ?? 0) > 0 ||
                s.mostRecommended.length > 0 ||
                s.mostCited.length > 0,
            ) ? (
              <>
                <h2 className="section-head">Leaders by Buyer-Journey Stage</h2>
                {/* Joan's wording verbatim (17-July doc §5). */}
                <p className="section-note">
                  This table shows the top companies AI surfaced at each
                  buyer-journey stage, separated by mentions, citations, and
                  recommendations. Use it to see who leads at each stage, which
                  signal drives that leadership, and where the client is gaining
                  or losing visibility.
                </p>
                <p className="section-note">
                  Each row should be read by stage. Leaders shown are the
                  strongest performers within that specific stage, not overall
                  report rankings. Bold indicates the client company; click any
                  company to see the answers behind it.
                </p>
                <table className="leads-table signal-leaders-table">
                  <thead>
                    <tr>
                      <th>
                        Stage <InfoTip text={TIP.stage} />
                      </th>
                      <th>
                        Most Mentions <InfoTip text={TIP.mostMentions} />
                      </th>
                      <th>
                        Most Cited <InfoTip text={TIP.mostCited} />
                      </th>
                      <th>
                        Most Recommended{' '}
                        <InfoTip text={TIP.mostRecommended} align="end" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.signalLeaders.map((s) => (
                      <tr key={s.stage}>
                        <td>{STAGE_LABELS[s.stage]}</td>
                        <td>
                          <LeaderCell
                            list={s.mostMentions ?? []}
                            onPick={(c) => applyFilter('appearance', c)}
                          />
                        </td>
                        <td>
                          <LeaderCell
                            list={s.mostCited}
                            onPick={(c) => applyFilter('appearance', c)}
                          />
                        </td>
                        <td>
                          <LeaderCell
                            list={s.mostRecommended}
                            onPick={(c) => applyFilter('recommendation', c)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* The race at every stage WITH the client in it (Lisa,
                    2026-09-08). Replaces the leaders' bump chart ("this report
                    isn't for Abcam ... they're not even in the top five") and
                    the gap dumbbell ("I don't understand it even more"). The
                    verdict beneath names the shortest path onto a leaderboard —
                    Ramos's "here's the visual, here's what it's saying, and
                    here's the recommendation". */}
                <StageStandings
                  standings={standings}
                  targetName={targetEntry?.company ?? run.companyName}
                />
                {/* The per-stage detail stays underneath as the reference. */}
                <StageLeaderGrid stages={aggregate.signalLeaders} />
                <TableInsight insight={interpretation?.tables.signalLeaders} />
              </>
            ) : null,

          stageSurfaced:
            aggregate &&
            aggregate.stageVisibility.entries.some(
              (s) => (s.surfaced?.length ?? 0) > 0,
            ) ? (
              <details className="stage-surfaced-details">
                <summary>Explore all suppliers surfaced by stage</summary>
                <p className="muted">
                  Every supplier the AI named at each buyer-journey stage — the
                  evidence that suppliers get surfaced <strong>early</strong>,
                  not only at the final shortlist, so content discoverable in
                  the early stages wins visibility. Bold = your tracked set; ★ =
                  actively recommended in that stage. Click a name to see the
                  answers behind it.
                </p>
                <div className="stage-surfaced">
                  {aggregate.stageVisibility.entries
                    .filter((s) => (s.surfaced?.length ?? 0) > 0)
                    .map((s) => (
                      <div key={s.stage} className="stage-surfaced-row">
                        <div className="stage-surfaced-label">
                          {STAGE_LABELS[s.stage]}
                        </div>
                        <div className="topic-chips">
                          {(s.surfaced ?? []).map((sup) => (
                            <button
                              key={sup.company}
                              type="button"
                              className={
                                isActiveFilter('appearance', sup.company)
                                  ? 'topic-chip topic-chip-active'
                                  : 'topic-chip topic-chip-button'
                              }
                              onClick={() =>
                                applyFilter('appearance', sup.company)
                              }
                              title={`Show the ${STAGE_LABELS[s.stage]} answers that named ${sup.company}`}
                            >
                              {sup.tracked ? (
                                <strong>{sup.company}</strong>
                              ) : (
                                sup.company
                              )}
                              {sup.recommendations > 0 ? ' ★' : ''}{' '}
                              <span className="topic-conf">
                                {sup.appearances}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </details>
            ) : null,

          surfacedSuppliers: aggregate ? (
            <>
              <h2 className="section-head">AI-Surfaced Supplier Detail</h2>
              {untrackedSuppliers.length === 0 &&
              untrackedDirectories.length === 0 ? (
                <p className="muted">
                  AI surfaced no suppliers beyond your tracked set.
                </p>
              ) : (
                <>
                  <p className="section-note">
                    This table shows your company alongside the suppliers AI
                    actually introduced into the benchmark responses, including
                    companies not included in the original tracked competitive
                    set. Raw visibility, mention, citation, and recommendation
                    counts show the volume and strength of each supplier’s
                    presence in AI-generated answers, helping show how the
                    client’s AI visibility compares with both known competitors
                    and AI-surfaced alternatives.
                  </p>
                  <p className="section-note">
                    <strong>Use this table to:</strong> Compare your company’s
                    actual AI presence against the suppliers AI is placing into
                    the buyer’s consideration path; identify overlooked
                    competitors or adjacent alternatives; validate whether the
                    tracked competitive set reflects how AI frames the category;
                    and prioritize deeper analysis of suppliers with high
                    visibility, citation, or recommendation counts. Click a
                    supplier to see the exact prompts, models, and AI answers
                    that named it.
                  </p>
                  <table className="leads-table">
                    <thead>
                      <tr>
                        <th>
                          Supplier <InfoTip text={TIP.supplier} />
                        </th>
                        <th>
                          Visibility <InfoTip text={TIP.visibleCol} />
                        </th>
                        <th>
                          Mentions <InfoTip text={TIP.mentionsCol} />
                        </th>
                        <th>
                          Citations <InfoTip text={TIP.citationsCol} />
                        </th>
                        <th>
                          Recommended <InfoTip text={TIP.recommended} />
                        </th>
                        {!clientView && <th>Flag</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(targetSurfaced
                        ? [targetSurfaced, ...untrackedSuppliers.slice(0, 12)]
                        : untrackedSuppliers.slice(0, 12)
                      ).map((s) => (
                        <tr
                          key={s.company}
                          className={
                            isActiveFilter('appearance', s.company)
                              ? 'row-active'
                              : undefined
                          }
                        >
                          <td className={s.isTarget ? 'lead-name' : undefined}>
                            <button
                              type="button"
                              className="supplier-link"
                              onClick={() =>
                                applyFilter('appearance', s.company)
                              }
                              title={`Show the AI answers that named ${s.company}`}
                            >
                              {s.company}
                            </button>
                            {s.isTarget ? ' (you)' : ''}
                          </td>
                          <td>{s.appearances}</td>
                          <td>{s.mentions ?? s.appearances}</td>
                          <td>{citedCell(s)}</td>
                          <td>{s.recommendations}</td>
                          {!clientView && (
                            <td>
                              {s.isTarget ? (
                                <span className="muted">—</span>
                              ) : (
                                <select
                                  className="flag-select"
                                  defaultValue=""
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    e.currentTarget.value = '';
                                    if (v) void flagSupplier(s.company, v);
                                  }}
                                  title={`Flag ${s.company} as not a valid competitor`}
                                >
                                  <option value="">⚑ Flag…</option>
                                  <option value="not_supplier">
                                    Not a supplier
                                  </option>
                                  <option value="defunct">Defunct brand</option>
                                  <option value="product">
                                    A product, not a company
                                  </option>
                                </select>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {untrackedDirectories.length > 0 && (
                    <>
                      <h3 className="section-subhead">
                        Supplier directories &amp; marketplaces surfaced
                      </h3>
                      <p className="muted">
                        Directories/marketplaces (Biocompare, CiteAb,
                        SelectScience…) where buyers <em>find</em> suppliers —
                        shown separately so they don’t read as competitors. A
                        directory you <em>name</em> as a competitor is tracked
                        like any other company and appears in the tables above.
                      </p>
                      <table className="leads-table">
                        <thead>
                          <tr>
                            <th>Directory / marketplace</th>
                            <th>
                              Visibility <InfoTip text={TIP.visibleCol} />
                            </th>
                            <th>
                              Mentions <InfoTip text={TIP.mentionsCol} />
                            </th>
                            <th>
                              Citations <InfoTip text={TIP.citationsCol} />
                            </th>
                            <th>
                              Recommended <InfoTip text={TIP.recommended} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {untrackedDirectories.slice(0, 12).map((s) => (
                            <tr key={s.company}>
                              <td>
                                <button
                                  type="button"
                                  className="supplier-link"
                                  onClick={() =>
                                    applyFilter('appearance', s.company)
                                  }
                                  title={`Show the AI answers that named ${s.company}`}
                                >
                                  {s.company}
                                </button>
                              </td>
                              <td>{s.appearances}</td>
                              <td>{s.mentions ?? s.appearances}</td>
                              <td>{citedCell(s)}</td>
                              <td>{s.recommendations}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  {/* Where the audience is actually lost, against the leader.
                      A quantity ("10 answers") is not actionable; a funnel
                      localises the loss to a step. */}
                  <VisibilityFunnel
                    funnels={funnels}
                    comparators={funnelChoices}
                    context={funnelCtx}
                    verdict={funnelText}
                    selected={funnelRivalEffective}
                    onSelect={setFunnelRival}
                  />
                  <TableInsight
                    insight={interpretation?.tables.surfacedSuppliers}
                  />
                </>
              )}
            </>
          ) : null,

          modelBreakdown:
            aggregate && aggregate.modelBreakdown.entries.length > 1 ? (
              <>
                <h2 className="section-head">Per-Model Visibility Breakdown</h2>
                {/* Joan 17-July §6: keep this table but position it as a
                    DIAGNOSTIC/detail view, shorten the intro, and avoid
                    overclaiming how models work internally. */}
                <p className="section-note">
                  A diagnostic view: how visibility varied across each AI model
                  in the panel. Model-level variation suggests differences in
                  retrieval behaviour, cited sources, or category association —
                  it does not tell us how any model works internally.
                </p>
                <p className="section-note">
                  <strong>Use this data to:</strong> Review the prompts where
                  the company is less visible in a given model, and compare
                  which sources, competitors, and content types those models
                  cited instead.
                </p>
                <p className="muted">
                  Each row is one AI model. “Scored” is the number of prompt
                  responses tested for that model. Mention, Citation and
                  Recommendation are measured independently — one answer can
                  count in all three — so they overlap and do not add up to
                  “Scored”.
                </p>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>
                        Model <InfoTip text={TIP.model} />
                      </th>
                      <th>
                        Mode <InfoTip text={TIP.mode} />
                      </th>
                      <th>
                        Scored <InfoTip text={TIP.scored} />
                      </th>
                      <th>
                        No Mention <InfoTip text={TIP.noMention} />
                      </th>
                      <th>
                        Mention <InfoTip text={TIP.mention} />
                      </th>
                      <th>
                        Citation <InfoTip text={TIP.citation} />
                      </th>
                      <th>
                        Recommendation <InfoTip text={TIP.recommendation} />
                      </th>
                      <th>
                        Cited as a source{' '}
                        <InfoTip text={TIP.citedRate} align="end" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.modelBreakdown.entries.map((m) => (
                      <tr key={m.model}>
                        <td>{m.model}</td>
                        {/* Fall back to citation>0 for aggregates written
                            before `grounded` existed. */}
                        <td>{modePill(m.grounded ?? m.citation > 0)}</td>
                        <td>{m.scoredCells}</td>
                        <td>{m.noMention}</td>
                        <td>{m.mention}</td>
                        <td>{m.citation}</td>
                        <td>{m.recommendation}</td>
                        {/* Of the answers this model backed with ANY source, how
                            many cited the target. A model that cited nothing has
                            no denominator, so it reads "—", never a fabricated
                            0% (D3) — 30 of 44 runs are web-off. */}
                        <td>
                          {m.sourcedAnswers && m.sourcedAnswers > 0
                            ? `${m.citation} / ${m.sourcedAnswers} (${pct(
                                m.citation / m.sourcedAnswers,
                              )})`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="muted">
                  <strong>How to read this:</strong> Memory-driven models answer
                  from training memory with no live sources — their
                  recommendations reflect how deeply {run.companyName} is baked
                  into the model, which is durable but slow to shift.
                  Retrieval-grounded models cite live sources — their
                  recommendations are influenceable through the content and
                  citations AI can find (see source intelligence below). A
                  strong memory-driven presence with little retrieval grounding
                  means your AI visibility is largely frozen-in; strong
                  retrieval grounding means it is movable.
                </p>
                <p className="muted">
                  A memory-driven model can still show a Citation count: for a
                  model without web access, a “citation” means it phrased an
                  answer as if referencing a source, even though no live,
                  retrievable source was captured. Mode is keyed off whether
                  real sources actually appeared, so it remains the more
                  reliable signal of grounding.
                </p>
                {/* One square per answer the model actually produced. Replaces
                    a grouped bar of these same counts — at 0-5 out of 26, bar
                    length carried nothing, and a zero-length bar is not a
                    finding you can see. An empty row is. */}
                <ModelReachGrid
                  entries={aggregate.modelBreakdown.entries}
                  targetName={targetEntry?.company ?? run.companyName}
                />
                <TableInsight insight={interpretation?.tables.modelBreakdown} />
              </>
            ) : null,

          recommendationTally:
            aggregate && aggregate.recommendationTally.length > 0 ? (
              <>
                <h2 className="section-head">Who AI Recommends</h2>
                <p className="section-note">
                  A recommendation is the strongest signal in the rubric: AI
                  actively put the company forward as a supplier choice. Click a
                  company to see the exact answers behind its number.
                </p>
                {/* The ranked table is the analysis. Where an aggregate predates
                    it (no recommendationLeaders), fall back to the original chip
                    cloud so an old run still shows its tally. */}
                {aggregate.recommendationLeaders?.length > 0 ? (
                  <table className="leads-table">
                    <thead>
                      <tr>
                        <th>
                          Rank <InfoTip text={TIP.recRank} />
                        </th>
                        <th>
                          Supplier <InfoTip text={TIP.company} />
                        </th>
                        <th>
                          Recommendations <InfoTip text={TIP.recommended} />
                        </th>
                        <th>
                          Share of all <InfoTip text={TIP.recShare} />
                        </th>
                        <th>
                          Strongest stage <InfoTip text={TIP.strongestStage} />
                        </th>
                        <th>
                          Why it matters{' '}
                          <InfoTip text={TIP.whyItMatters} align="end" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {aggregate.recommendationLeaders.map((r) => (
                        <tr
                          key={r.company}
                          className={
                            isActiveFilter('recommendation', r.company)
                              ? 'row-active'
                              : undefined
                          }
                        >
                          <td>{r.rank}</td>
                          <td className={r.isTarget ? 'lead-name' : undefined}>
                            <button
                              type="button"
                              className="supplier-link"
                              onClick={() =>
                                applyFilter('recommendation', r.company)
                              }
                              title={`Show the AI answers that recommended ${r.company}`}
                            >
                              {r.company}
                            </button>
                            {r.isTarget ? ' (you)' : ''}
                            {!r.tracked && !r.isTarget ? (
                              <span className="muted"> · not tracked</span>
                            ) : null}
                          </td>
                          <td>{r.recommendations}</td>
                          <td>{pct(r.shareOfRecommendations)}</td>
                          <td>
                            {r.strongestStage
                              ? STAGE_LABELS[r.strongestStage]
                              : '—'}
                          </td>
                          <td className="muted">{r.whyItMatters}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="topic-chips">
                    {aggregate.recommendationTally.slice(0, 12).map((t) => (
                      <button
                        key={t.company}
                        type="button"
                        className={
                          isActiveFilter('recommendation', t.company)
                            ? 'topic-chip topic-chip-active'
                            : 'topic-chip topic-chip-button'
                        }
                        onClick={() => applyFilter('recommendation', t.company)}
                        title={`Show the AI answers that recommended ${t.company}`}
                      >
                        {t.company}{' '}
                        <span className="topic-conf">{t.count}×</span>
                      </button>
                    ))}
                  </div>
                )}
                {aggregate.recommendationTally.length > 10 && (
                  <details className="all-recommended">
                    <summary>
                      See all {aggregate.recommendationTally.length} companies
                      AI recommended
                    </summary>
                    <div className="topic-chips">
                      {aggregate.recommendationTally.map((t) => (
                        <button
                          key={t.company}
                          type="button"
                          className={
                            isActiveFilter('recommendation', t.company)
                              ? 'topic-chip topic-chip-active'
                              : 'topic-chip topic-chip-button'
                          }
                          onClick={() =>
                            applyFilter('recommendation', t.company)
                          }
                          title={`Show the AI answers that recommended ${t.company}`}
                        >
                          {t.company}{' '}
                          <span className="topic-conf">{t.count}×</span>
                        </button>
                      ))}
                    </div>
                  </details>
                )}
                <TableInsight
                  insight={interpretation?.tables.recommendationTally}
                />
              </>
            ) : null,

          citationSources:
            aggregate && aggregate.citationSources.length > 0 ? (
              <>
                <h2 className="section-head">
                  Source Types Shaping AI Answers
                </h2>
                {/* Joan's wording verbatim (17-July doc §7), shared with the
                    Markdown export. Deliberately avoids "relies on" /
                    "shaping" as causal claims. */}
                <p className="section-note">{COPY.citationSources.intro}</p>
                <p className="section-note">
                  <strong>Use this data to:</strong>{' '}
                  {COPY.citationSources.useThisData}{' '}
                  {/* Screen-only affordance: the Markdown export has no
                      expandable domain drill-down to point at. */}
                  Drill down into each source type to see the actual domains
                  cited.
                </p>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>
                        Source type <InfoTip text={TIP.srcType} />
                      </th>
                      <th>
                        Answers citing this source{' '}
                        <InfoTip text={TIP.srcAppearances} />
                      </th>
                      <th>
                        Total citations <InfoTip text={TIP.srcCitations} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.citationSources.slice(0, 12).map((c) => (
                      <Fragment key={c.sourceType}>
                        <tr>
                          <td>{c.sourceType}</td>
                          <td>{c.appearances}</td>
                          <td>{c.citations ?? '—'}</td>
                        </tr>
                        {c.domains && c.domains.length > 0 ? (
                          <tr className="source-domains-row">
                            <td colSpan={3}>
                              <details className="source-domains">
                                <summary>
                                  Top cited domains in {c.sourceType} (
                                  {c.domains.length})
                                </summary>
                                <table className="leads-table source-domains-table">
                                  <thead>
                                    <tr>
                                      <th>Domain</th>
                                      <th>Answers citing this source</th>
                                      <th>Total citations</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.domains.map((d) => (
                                      <tr key={d.domain}>
                                        <td>{d.domain}</td>
                                        <td>{d.appearances}</td>
                                        <td>{d.citations ?? '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </details>
                            </td>
                          </tr>
                        ) : null}
                        {c.pageTypes && c.pageTypes.length > 0 ? (
                          <tr className="source-domains-row">
                            <td colSpan={3}>
                              <details className="source-domains">
                                <summary>
                                  Within {c.sourceType}, by content type
                                  (each cited page classified by its primary
                                  purpose)
                                </summary>
                                <table className="leads-table source-domains-table">
                                  <thead>
                                    <tr>
                                      <th>Supplier content type</th>
                                      <th>Answers citing this type</th>
                                      <th>Total citations</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.pageTypes.map((pt) => (
                                      <tr key={pt.pageType}>
                                        <td>{pt.pageType}</td>
                                        <td>{pt.appearances}</td>
                                        <td>{pt.citations}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </details>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                {/* Part-to-whole share of every cited link, capped at six
                    segments with the tail folded into "Other" — Lisa's ask
                    (2026-09-01): "a pie chart of all the source types to begin
                    with. How much is supplier content? How much is peer
                    reviewed?". A pie is legitimate here specifically because
                    each cited URL is classified into exactly ONE source type, so
                    the slices genuinely sum to a whole. The bar it replaces
                    re-plotted table rows against a raw citation count. */}
                <SourceDonut slices={kpiSet.sourceShare} />
                <TableInsight
                  insight={interpretation?.tables.citationSources}
                />
              </>
            ) : null,

          topJournals:
            aggregate && aggregate.topJournals?.length > 0 ? (
              <>
                <h2 className="section-head">Top Journals AI Cites</h2>
                {/* Joan's wording verbatim (17-July doc §9), including the
                    methodology caveat — this table must not read as a complete
                    list of every cited journal. Shared with the Markdown
                    export, which was missing `whatTheTableShows` entirely. */}
                <p className="section-note">{COPY.topJournals.intro}</p>
                <p className="section-note">{COPY.topJournals.positioning}</p>
                <p className="section-note">
                  {COPY.topJournals.whatTheTableShows}
                </p>
                <p className="section-note">
                  <strong>Use this data to:</strong>{' '}
                  {COPY.topJournals.useThisData}
                </p>
                <p className="muted">
                  <strong>Methodology note:</strong>{' '}
                  {COPY.topJournals.methodologyNote}
                </p>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Journal</th>
                      <th>Domain</th>
                      <th>
                        Answers citing this source{' '}
                        <InfoTip text={TIP.srcAppearances} />
                      </th>
                      <th>
                        Total citations <InfoTip text={TIP.srcCitations} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.topJournals.slice(0, 20).map((j) => (
                      <tr key={j.journal}>
                        <td>{j.journal}</td>
                        <td className="muted">{j.domain ?? '—'}</td>
                        <td>{j.appearances}</td>
                        <td>{j.citations}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* The journal is the actionable unit — "AI favours this
                    journal, so publish there" — so rank it visually. */}
                <RankedBarList
                  data={aggregate.topJournals.slice(0, 12).map((j) => ({
                    label: j.journal,
                    value: j.appearances,
                    sublabel: j.domain,
                    tooltip: `${j.journal}${j.domain ? ` (${j.domain})` : ''}: cited in ${j.appearances} answers, ${j.citations} total citations`,
                  }))}
                  caption="Journals ranked by the number of AI answers that cited them."
                />
              </>
            ) : null,

          citationSourcesByModel:
            aggregate && aggregate.citationSourcesByModel?.length > 0 ? (
              <>
                <h2 className="section-head">Top Source Types by AI Model</h2>
                {/* "relied on" and "contributed to" are causal claims about a
                    model's internals that we cannot observe — Joan banned the
                    first by name (17-July §7). We report what each model
                    CITED. The Markdown export was corrected on 2026-07-19; the
                    client kept the banned wording live until 2026-07-19, which
                    is why both now render from one shared constant. */}
                <p className="section-note">{COPY.sourcesByModel.intro}</p>
                <p className="section-note">
                  <strong>Use this data to:</strong>{' '}
                  {COPY.sourcesByModel.useThisData}
                </p>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>
                        AI Model <InfoTip text={TIP.model} />
                      </th>
                      <th>
                        #1 Source Type <InfoTip text={TIP.srcType} />
                      </th>
                      <th>
                        % of Total <InfoTip text={TIP.srcShare} />
                      </th>
                      <th>#2 Source Type</th>
                      <th>% of Total</th>
                      <th>#3 Source Type</th>
                      <th>
                        % of Total <InfoTip text={TIP.srcShare} align="end" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.citationSourcesByModel.map((m) => {
                      // Each type's share of THIS model's total source activity,
                      // so the row reads as a composition rather than a raw count.
                      const total = m.sources.reduce(
                        (sum, c) => sum + c.appearances,
                        0,
                      );
                      const top3 = m.sources.slice(0, 3);
                      return (
                        <tr key={m.model}>
                          <td>{m.model}</td>
                          {[0, 1, 2].map((i) => {
                            const c = top3[i];
                            return (
                              <Fragment key={i}>
                                <td>{c ? c.sourceType : '—'}</td>
                                <td>
                                  {c && total > 0
                                    ? pct(c.appearances / total)
                                    : '—'}
                                </td>
                              </Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Lisa's follow-up to the donut: "do we then want one for each
                    one as well?" Colours match the donut, because the category
                    order is taken from the run-wide mix. */}
                <SourceMixByModel mixes={sourceMixes} />
              </>
            ) : null,

          promptThemes:
            aggregate && aggregate.promptThemes?.length > 0 ? (
              <>
                <h2 className="section-head">Prompt Theme Opportunities</h2>
                <p className="section-note">
                  Where the content gaps are. A <strong>theme</strong> is what a
                  prompt is <em>about</em> — a technology, workflow, or
                  application — independent of its buyer-journey stage. Biggest
                  gap first: these are the subjects where AI is answering
                  without you.
                </p>
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>
                        Theme <InfoTip text={TIP.theme} />
                      </th>
                      <th>Prompts</th>
                      <th>
                        Your visibility <InfoTip text={TIP.visibility} />
                      </th>
                      <th>Leader</th>
                      <th>
                        Gap <InfoTip text={TIP.themeGap} />
                      </th>
                      <th>
                        Evidence comes from{' '}
                        <InfoTip text={TIP.themeSources} align="end" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregate.promptThemes.slice(0, 12).map((t) => {
                      const leaderCompany = t.leaderCompany;
                      return (
                        <tr key={t.theme}>
                          <td>
                            {t.theme}{' '}
                            <span
                              className={
                                OPPORTUNITY_PILL[t.opportunity] ?? 'pill'
                              }
                            >
                              {t.opportunity}
                            </span>
                          </td>
                          <td>{t.prompts}</td>
                          <td>{pct(t.targetVisibilityRate)}</td>
                          <td>
                            {leaderCompany ? (
                              <>
                                <button
                                  type="button"
                                  className="supplier-link"
                                  onClick={() =>
                                    applyFilter('appearance', leaderCompany)
                                  }
                                  title={`Show the AI answers where ${leaderCompany} appeared`}
                                >
                                  {leaderCompany}
                                </button>{' '}
                                <span className="topic-conf">
                                  {pct(t.leaderVisibilityRate)}
                                </span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {t.gapToLeader > 0 ? pct(t.gapToLeader) : '—'}
                          </td>
                          <td className="muted">
                            {t.topSourceTypes.join(', ') || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TableInsight insight={interpretation?.tables.promptThemes} />
              </>
            ) : null,

          actionPlan: interpretation ? (
            <PriorityActionPlan
              actions={interpretation.actions}
              additionalOpportunities={
                interpretation.executive?.additionalOpportunities
              }
              followOnAnalysis={interpretation.executive?.followOnAnalysis}
            />
          ) : null,

          promptLevelResults:
            data && data.results.length > 0 ? (
              <div ref={resultsRef}>
                <h2 className="section-head">
                  <span>Prompt-Level Results</span>
                  {filter && (
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setFilter(null)}
                    >
                      Clear filter
                    </button>
                  )}
                </h2>
                {filter && (
                  <p className="form-notice">
                    Showing {visibleResults.length} cell
                    {visibleResults.length === 1 ? '' : 's'} where{' '}
                    <strong>{filter.company}</strong>{' '}
                    {filter.kind === 'recommendation'
                      ? 'was recommended'
                      : 'appeared'}
                    , out of {allResults.length} total — the exact prompts,
                    models, and AI answers behind that number.
                  </p>
                )}
                {/* Lisa, 2026-09-01, on this table: "this is a long table, but
                    I feel like there may be a better way to represent that."
                    The grid shows the shape of the run at a glance; the table
                    below stays the source of truth. Deliberately NOT filtered —
                    it is the overview the filter drills into. */}
                <PromptOutcomeHeatmap
                  prompts={data.prompts}
                  results={allResults}
                  models={run.modelPanel}
                  targetName={targetEntry?.company ?? run.companyName}
                  visibleCount={targetEntry?.visibleCount}
                />
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>
                        Stage <InfoTip text={TIP.promptStage} />
                      </th>
                      <th>
                        Prompt <InfoTip text={TIP.prompt} />
                      </th>
                      <th>
                        Model <InfoTip text={TIP.promptModel} />
                      </th>
                      <th>
                        {run.companyName} <InfoTip text={TIP.targetClass} />
                      </th>
                      <th>
                        Recommended <InfoTip text={TIP.recommendedCompany} />
                      </th>
                      <th>
                        Others named{' '}
                        <InfoTip text={TIP.othersNamed} align="end" />
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleResults.map((r) => {
                      const prompt = promptById.get(r.promptId);
                      const cellKey = `${r.promptId}#${r.model}#${r.runIndex}`;
                      const response = responseByCell.get(cellKey);
                      const open = openRow === r.id;
                      return (
                        <Fragment key={r.id}>
                          <tr>
                            <td>{prompt ? STAGE_LABELS[prompt.stage] : '—'}</td>
                            <td>{prompt?.prompt ?? '—'}</td>
                            <td>
                              {r.model}
                              {run.conditions.runsPerPrompt > 1
                                ? ` #${r.runIndex + 1}`
                                : ''}
                            </td>
                            <td>{classPill(r.targetClassification)}</td>
                            <td>{r.recommendedCompany ?? '—'}</td>
                            <td>
                              {r.otherSuppliers.length > 0 ? (
                                <button
                                  type="button"
                                  className="supplier-link"
                                  onClick={() => setOpenRow(open ? null : r.id)}
                                  title={`${r.otherSuppliers.length} untracked supplier${
                                    r.otherSuppliers.length === 1 ? '' : 's'
                                  } named — click for the full list`}
                                >
                                  {r.otherSuppliers.length}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-small"
                                onClick={() => setOpenRow(open ? null : r.id)}
                              >
                                {open ? 'Hide' : 'Details'}
                              </button>
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={7}>
                                <div className="card">
                                  {Object.keys(r.competitorClassifications)
                                    .length > 0 && (
                                    <p>
                                      {Object.entries(
                                        r.competitorClassifications,
                                      ).map(([name, c]) => (
                                        <span
                                          key={name}
                                          style={{ marginRight: 12 }}
                                        >
                                          {name}: {classPill(c)}
                                        </span>
                                      ))}
                                    </p>
                                  )}
                                  <p className="muted">
                                    Suppliers named:{' '}
                                    {r.fullSupplierSet.length > 0
                                      ? r.fullSupplierSet.map((name, i) => (
                                          <Fragment key={name}>
                                            {i > 0 ? ', ' : ''}
                                            <span
                                              className={
                                                filterKey &&
                                                supplierKey(name) === filterKey
                                                  ? 'supplier-hit'
                                                  : undefined
                                              }
                                            >
                                              {name}
                                            </span>
                                          </Fragment>
                                        ))
                                      : 'none'}
                                    {r.sources.length > 0 && (
                                      <> · Sources: {r.sources.join('; ')}</>
                                    )}
                                  </p>
                                  {r.supplierOwnedSources.length > 0 && (
                                    <p className="muted">
                                      Supplier-owned sources cited:{' '}
                                      {r.supplierOwnedSources.join(', ')}
                                    </p>
                                  )}
                                  <p style={{ whiteSpace: 'pre-wrap' }}>
                                    {response?.responseText
                                      ? highlightResponse(
                                          response.responseText,
                                          [
                                            run.companyName,
                                            ...Object.keys(
                                              r.competitorClassifications,
                                            ),
                                            // Only suppliers the aggregate
                                            // confirmed — never stale tool names.
                                            ...r.fullSupplierSet.filter((n) =>
                                              confirmedSupplierKeys.has(
                                                supplierKey(n),
                                              ),
                                            ),
                                          ],
                                          filterKey,
                                        )
                                      : 'No response provided'}
                                  </p>
                                  {(() => {
                                    // Prefer the fully-resolved per-source legend
                                    // (captured for EVERY model); fall back to raw
                                    // provider citations for un-backfilled runs.
                                    const cites =
                                      r.structuredCitations &&
                                      r.structuredCitations.length > 0
                                        ? r.structuredCitations
                                        : (response?.citations ?? []).map(
                                            (c, i) => ({
                                              citationMarker: `[${i + 1}]`,
                                              sourceReference:
                                                c.url ?? c.title ?? '',
                                              sourceTitle: c.title,
                                              sourceDescriptiveText: null,
                                              url: c.url,
                                              origin: 'provider',
                                              journalName: null,
                                            }),
                                          );
                                    if (cites.length === 0) return null;
                                    return (
                                      <>
                                        <p className="muted citation-list-head">
                                          Cited sources ({cites.length})
                                        </p>
                                        <ol className="citation-list">
                                          {cites.map((c, i) => (
                                            <li key={i}>
                                              <span className="citation-num">
                                                {c.citationMarker ??
                                                  `[${i + 1}]`}
                                              </span>{' '}
                                              {c.sourceTitle &&
                                              c.sourceTitle !==
                                                c.sourceReference
                                                ? `${c.sourceTitle} — `
                                                : ''}
                                              {c.url ? (
                                                <a
                                                  href={c.url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                >
                                                  {c.url}
                                                </a>
                                              ) : (
                                                c.sourceReference
                                              )}
                                              {c.journalName && (
                                                <span className="citation-journal">
                                                  {' '}
                                                  · <em>{c.journalName}</em>
                                                </span>
                                              )}
                                              {c.sourceDescriptiveText && (
                                                <span className="citation-desc">
                                                  {' '}
                                                  — {c.sourceDescriptiveText}
                                                </span>
                                              )}
                                            </li>
                                          ))}
                                        </ol>
                                      </>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <p className="muted">
                  Results are a dated snapshot: AI answers vary by model
                  version, web access, and run conditions. This run recorded its
                  conditions and repeats prompts to measure stability rather
                  than claim permanence.
                </p>
              </div>
            ) : null,
        };

        // The configured render order (enabled only), falling back to the
        // code default until the per-client layout config loads.
        const orderedKeys: ReportSectionKey[] = layout
          ? [...layout.sections]
              .filter((s) => s.enabled)
              .sort((a, b) => a.order - b.order)
              .map((s) => s.key)
          : DEFAULT_SECTION_ORDER;

        // In the client/demo view, also hide the interpretive content Joan's
        // 20-July demo markup pulls out: the LLM "What This Report Means"
        // synthesis and the Priority Action Plan (the latter "needs work" and is
        // being reworked separately — its Potential Follow-On Analysis renders
        // inside it, so it goes too). This is a view filter over the SAME
        // sections, not a config/per-client change — internal view is untouched.
        const CLIENT_HIDDEN_SECTIONS: ReadonlySet<ReportSectionKey> = new Set([
          'executiveSynthesis',
          'actionPlan',
        ]);
        const visibleKeys = clientView
          ? orderedKeys.filter((k) => !CLIENT_HIDDEN_SECTIONS.has(k))
          : orderedKeys;

        // The scrollable report: structural header (always shown) + the
        // config-ordered sections.
        const reportBody = (
          <>
            {aggregate && (
              <>
                {/* The decision KPI band — the first thing a reader sees
                    (2026-09-08). Lisa, 2026-09-01: visuals belong "at the top",
                    and the report has to survive a leadership reader who will
                    not work through the tables. These five answer "where do we
                    stand and what do we do", which no single table stated. */}
                <KpiBand
                  kpis={kpiSet.kpis}
                  note={
                    showDenominatorNote
                      ? `Based on ${scoredAnswers} scored AI answers — ${promptCount} prompts across ${stageCount} buyer-journey stages, each asked of ${modelCount} AI ${modelCount === 1 ? 'model' : 'models'}.`
                      : undefined
                  }
                />
                {/* The three scorecard tiles (AI visibility / recommendation
                    rate / result stability). Hidden in the client/demo view per
                    Ramos 2026-07-25. */}
                {!clientView && (
                  <BenchmarkScorecard run={run} aggregate={aggregate} />
                )}
                {/* Framing before figures: what the benchmark is, how it was run,
                    and what it measures (Joan's copy). Structural, not a
                    toggleable section — it carries no data. Hidden in the
                    client/demo view per Joan's 20-July markup. */}
                {!clientView && (
                  <BenchmarkAbout
                    shape={{ promptCount, stageCount, modelCount }}
                  />
                )}
                {!running && !clientView && !archived && (
                  <div className="interp-actions">
                    <input
                      type="text"
                      className="interp-guidance"
                      value={guidance}
                      onChange={(e) => setGuidance(e.target.value)}
                      placeholder="Optional steer for the summary, e.g. “shorter, lead with the competitive gap”"
                      disabled={regenerating}
                    />
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => void onRegenerateInterpretation()}
                      disabled={regenerating}
                    >
                      {regenerating
                        ? 'Generating…'
                        : interpretation
                          ? 'Regenerate interpretation'
                          : 'Generate interpretation'}
                    </button>
                  </div>
                )}
                {!clientView && <BenchmarkLegend />}
              </>
            )}
            {visibleKeys.map((key) => (
              <Fragment key={key}>{sectionNodes[key]}</Fragment>
            ))}
            {/* Top cited domains, regardless of source category — INTERNAL ONLY
                for now (Ramos 2026-07-25). Derived client-side: each source
                category already carries its per-host citation counts, and every
                cited URL lands in exactly ONE category, so summing a host's
                `citations` across categories is its true total (no double-count).
                The per-category domain lists are capped at 20, but a top-10
                domain is by definition high-volume and sits near the top of its
                category, so the cap doesn't affect this ranking. If this becomes
                client-facing, promote it to a server-side `topDomains` aggregate
                (mirrors topJournals) to drop the cap entirely. */}
            {!clientView &&
              aggregate &&
              (() => {
                const byDomain = new Map<string, number>();
                for (const c of aggregate.citationSources) {
                  for (const d of c.domains ?? []) {
                    byDomain.set(
                      d.domain,
                      (byDomain.get(d.domain) ?? 0) + (d.citations ?? 0),
                    );
                  }
                }
                const top = [...byDomain.entries()]
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10);
                if (top.length === 0) return null;
                return (
                  <>
                    <h2 className="section-head">
                      Top Cited Domains{' '}
                      <span
                        className="muted"
                        style={{ fontWeight: 400, fontSize: '0.8rem' }}
                      >
                        · internal only
                      </span>
                    </h2>
                    <p className="section-note">
                      The 10 domains AI cited most across this run, ranked by
                      total citations (volume), regardless of source category —
                      aggregated from the per-category domain breakdowns.
                    </p>
                    <table className="leads-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Domain</th>
                          <th>Total citations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {top.map(([domain, citations], i) => (
                          <tr key={domain}>
                            <td className="muted">{i + 1}</td>
                            <td>{domain}</td>
                            <td>{citations}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                );
              })()}
          </>
        );

        // While the run is still working there is no review panel — render the
        // report full-width. The client/demo view also renders full-width: it
        // hides the internal review/curation aside entirely (feedback, add
        // supplier, layout settings). Only the internal view gets the aside.
        if (running || clientView) return reportBody;

        return (
          <div className="benchmark-report-split">
            <aside className="benchmark-review-col">
              {!archived && (
                <BenchmarkFeedbackPanel
                  runId={run.id}
                  onApplied={() => void load()}
                />
              )}
              <AddSupplierPanel />
              {layout && (
                <ReportLayoutSettings layout={layout} onSaved={setLayout} />
              )}
            </aside>
            <div className="benchmark-report-col">{reportBody}</div>
          </div>
        );
      })()}
    </div>
  );
}
